
import React, { useState, useEffect, useContext, useMemo, useCallback, useRef } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Search, User, ShoppingCart, Menu, X } from "lucide-react";
import { AuthContext } from "../context/AuthContext";
import { api, apiPaths } from '../config/api';
import { useDebounce } from "../hooks/useDebounce";

// Client-side suggestion cache: 30s TTL
const SUGGESTION_CACHE_TTL_MS = 30000;
const suggestionCache = new Map();
const suggestionCacheExpiry = new Map();

function Header() {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, isAuthenticated, logout } = useContext(AuthContext);
  const [searchOpen, setSearchOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchSuggestions, setSearchSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [searchLoading, setSearchLoading] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const abortControllerRef = useRef(null);
  const userMenuRef = useRef(null);
  const debouncedSearchQuery = useDebounce(searchQuery, 300);

  // Redirect authenticated users away from auth pages
  useEffect(() => {
    if (isAuthenticated && (location.pathname === '/login' || location.pathname === '/signup')) {
      navigate('/');
    }
  }, [isAuthenticated, location.pathname, navigate]);

  // Close mobile menu when route changes
  useEffect(() => {
    setMobileMenuOpen(false);
    setUserMenuOpen(false);
  }, [location.pathname]);

  // Close user menu when clicking outside
  useEffect(() => {
    if (!userMenuOpen) return undefined;

    const handleClickOutside = (event) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target)) {
        setUserMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [userMenuOpen]);

  // Base navigation items - memoized
  const baseNavItems = useMemo(() => [
    { label: "Home", path: "/" },
    { label: "Category", path: "/category" },
    { label: "Products", path: "/product" },
    { label: "About Us", path: "/about" },
  ], []);

  // Only customers (not admin) see Cart/Query
  const isCustomer = useMemo(() => {
    if (!isAuthenticated || !user) return false;
    const r = String(user.role || "").trim().toLowerCase();
    return r !== "admin" && r !== "super admin";
  }, [isAuthenticated, user]);

  const isAdmin = useMemo(() => {
    if (!isAuthenticated || !user) return false;
    const r = String(user.role || "").trim().toLowerCase();
    return r === "admin" || r === "super admin";
  }, [isAuthenticated, user]);

  // Conditional navigation items: Cart/Query only for logged-in customers
  const navItems = useMemo(() => 
    isCustomer 
      ? [...baseNavItems, { label: "Cart/Query", path: "/cart" }]
      : baseNavItems,
    [isCustomer, baseNavItems]
  );

  // Memoized event handlers
  const handleProfileClick = useCallback(() => {
    setUserMenuOpen(false);
    navigate('/profile');
  }, [navigate]);

  const handleLogout = useCallback(() => {
    setUserMenuOpen(false);
    logout();
    navigate(isAdmin ? '/admin/login' : '/');
  }, [logout, navigate, isAdmin]);

  const toggleUserMenu = useCallback(() => {
    setUserMenuOpen((prev) => !prev);
  }, []);

  const renderUserMenu = (onItemClick) => (
    <div className="py-1 overflow-hidden rounded-lg border border-gray-700 bg-gray-900 shadow-lg min-w-[140px]">
      {!isAdmin && (
        <button
          type="button"
          onClick={() => {
            onItemClick?.();
            handleProfileClick();
          }}
          className="block px-4 py-2 w-full text-sm text-left text-white transition hover:bg-red-600"
        >
          Profile
        </button>
      )}
      <button
        type="button"
        onClick={() => {
          onItemClick?.();
          handleLogout();
        }}
        className="block px-4 py-2 w-full text-sm text-left text-white transition hover:bg-red-600"
      >
        Logout
      </button>
    </div>
  );

  const toggleMobileMenu = useCallback(() => {
    setMobileMenuOpen(prev => !prev);
  }, []);

  const closeMobileMenu = useCallback(() => {
    setMobileMenuOpen(false);
  }, []);

  // Search suggestions (debounced + lightweight /suggest endpoint + client cache + request cancellation)
  useEffect(() => {
    const query = debouncedSearchQuery.trim();

    // Cancel any in-flight request when query changes / clears
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }

    if (query.length < 2) {
      setSearchLoading(false);
      setSearchSuggestions([]);
      setShowSuggestions(false);
      return;
    }

    // Check client-side cache first (30s TTL)
    const cacheKey = query.toLowerCase();
    const cached = suggestionCache.get(cacheKey);
    const expiry = suggestionCacheExpiry.get(cacheKey);
    if (cached && expiry && Date.now() < expiry) {
      setSearchSuggestions(cached);
      setShowSuggestions(true);
      setSearchLoading(false);
      return;
    }

    const controller = new AbortController();
    abortControllerRef.current = controller;

    const fetchSuggestions = async () => {
      try {
        setSearchLoading(true);
        const response = await api.get(apiPaths.productsSuggest(query, 5), {
          signal: controller.signal,
        });

        const products = Array.isArray(response.data) ? response.data : [];
        const sliced = products.slice(0, 5);

        suggestionCache.set(cacheKey, sliced);
        suggestionCacheExpiry.set(cacheKey, Date.now() + SUGGESTION_CACHE_TTL_MS);

        setSearchSuggestions(sliced);
        setShowSuggestions(true);
      } catch (error) {
        if (error?.code === "ERR_CANCELED") return;
        setSearchSuggestions([]);
      } finally {
        if (abortControllerRef.current === controller) {
          setSearchLoading(false);
        }
      }
    };

    fetchSuggestions();

    return () => {
      controller.abort();
      if (abortControllerRef.current === controller) {
        abortControllerRef.current = null;
      }
    };
  }, [debouncedSearchQuery]);

  const handleSearchInputChange = useCallback((e) => {
    const value = e.target.value;
    setSearchQuery(value);
    if (value.trim().length >= 2) {
      setShowSuggestions(true);
    }
  }, []);

  const handleSearchSubmit = useCallback((e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/product?search=${encodeURIComponent(searchQuery.trim())}`);
      setSearchQuery("");
      setSearchSuggestions([]);
      setShowSuggestions(false);
      setSearchOpen(false);
    }
  }, [searchQuery, navigate]);

  const handleSuggestionClick = useCallback((product) => {
    navigate(`/product/${product.slug || product.id}`);
    setSearchQuery("");
    setSearchSuggestions([]);
    setShowSuggestions(false);
    setSearchOpen(false);
  }, [navigate]);

  const handleSearchBlur = useCallback(() => {
    setTimeout(() => {
      setShowSuggestions(false);
    }, 200);
  }, []);

  return (
    <header className="relative text-white bg-black">
      {/* MAIN DESKTOP CONTAINER 
        Uses lg:flex to put Logo (Left) next to Content (Right) 
      */}
      <div className="flex flex-col px-4 py-3 sm:px-6 lg:flex-row lg:items-center lg:justify-between">
        
        {/* --- LEFT SECTION: Logo & Mobile Toggles --- */}
        <div className="flex justify-between items-center lg:w-auto">
          {/* Logo */}
          <div className="flex-shrink-0">
            <Link to="/">
                <img 
                src="/logo.png"
                alt="Al Sayyed Electro Mech Engineering"
                // Adjusted width/height for better "span" effect
                className="w-full max-w-[150px] h-auto object-contain" 
                />
            </Link>
          </div>

          {/* Mobile Toggles (Search, Menu) - Visible ONLY on Mobile */}
          <div className="flex gap-2 items-center lg:hidden">
            {/* Mobile Search Toggle */}
            <button
              onClick={() => setSearchOpen(!searchOpen)}
              className="p-2 rounded-full transition hover:bg-red-600"
              title="Search"
            >
              <Search className="w-5 h-5" />
            </button>

            {/* Mobile Auth Icons - Cart only for customers */}
            {isCustomer && (
              <button
                onClick={() => navigate('/cart')}
                className="p-2 rounded-full transition hover:bg-red-600"
                title="Cart"
              >
                <ShoppingCart className="w-5 h-5" />
              </button>
            )}

            {/* Hamburger Menu */}
            <button
              onClick={toggleMobileMenu}
              className="p-2 rounded-full transition hover:bg-red-600"
              title="Menu"
            >
              {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>
        </div>

        {/* --- RIGHT SECTION: Search/Auth + Nav (Stacked Column) --- */}
        {/* Hidden on Mobile, Flex Column on Desktop */}
        <div className="hidden flex-col flex-grow items-end pl-8 space-y-3 lg:flex">
          
          {/* TOP ROW: Search Bar & Auth */}
          <div className="flex gap-4 justify-end items-center w-full">
            {/* Search */}
            <div className="relative">
              <form onSubmit={handleSearchSubmit} className="flex items-center bg-white rounded-full px-4 py-0.5 w-56 xl:w-80 focus-within:outline-none focus-within:ring-0">
                <button
                  type="submit"
                  className="w-4 h-4 text-black transition-colors cursor-pointer outline-none hover:text-gray-600"
                  title="Search"
                >
                  <Search className="w-4 h-4" />
                </button>
                <input
                  type="text"
                  aria-label="Search products"
                  placeholder="Search products..."
                  value={searchQuery}
                  onChange={handleSearchInputChange}
                  onBlur={handleSearchBlur}
                  onFocus={() => searchSuggestions.length > 0 && setShowSuggestions(true)}
                  className="flex-1 ml-2 text-sm text-black bg-transparent border-0 outline-none focus:outline-none focus:ring-0"
                />
                {searchLoading && (
                  <div className="ml-2 w-4 h-4 rounded-full border-b-2 border-gray-600 animate-spin"></div>
                )}
              </form>
              
              {/* Search Suggestions Dropdown */}
              {showSuggestions && searchSuggestions.length > 0 && (
                <div className="overflow-y-auto absolute right-0 left-0 top-full z-50 mt-1 max-h-60 bg-white rounded-lg border border-gray-200 shadow-lg">
                  {searchSuggestions.map((product) => (
                    <div
                      key={product.id}
                      onClick={() => handleSuggestionClick(product)}
                      className="px-4 py-3 text-black border-b border-gray-100 cursor-pointer hover:bg-gray-100 last:border-b-0"
                    >
                      <div className="text-sm font-medium text-gray-900">{product.name}</div>
                      <div className="text-xs text-gray-500">Product ID: {product.id}</div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* When no one is logged in: show Sign In + Register. When logged in: show Cart (customers) + account menu. */}
            {!isAuthenticated ? (
              <div className="flex gap-2 items-center">
                <button
                  onClick={() => navigate("/login")}
                  className="px-3 py-1 text-sm font-semibold rounded-full border-2 border-transparent transition hover:border-white hover:bg-red-600"
                >
                  Sign In
                </button>
                <button
                  onClick={() => navigate("/signup")}
                  className="px-3 py-1 text-sm font-semibold rounded-full border-2 border-transparent transition hover:border-white hover:bg-red-600"
                >
                  Register
                </button>
              </div>
            ) : (
              <div className="flex gap-2 items-center">
                {isCustomer && (
                  <button
                    onClick={() => navigate('/cart')}
                    className="p-2 rounded-full transition hover:bg-red-600"
                    title="Cart"
                  >
                    <ShoppingCart className="w-5 h-5" />
                  </button>
                )}
                <div className="relative" ref={userMenuRef}>
                  <button
                    type="button"
                    onClick={toggleUserMenu}
                    className={`p-2 rounded-full transition ${
                      userMenuOpen ? 'bg-red-600' : 'hover:bg-red-600'
                    }`}
                    title={isAdmin ? 'Account' : 'Profile'}
                    aria-expanded={userMenuOpen}
                    aria-haspopup="true"
                  >
                    <User className="w-5 h-5" />
                  </button>
                  {userMenuOpen && (
                    <div className="absolute right-0 top-full z-50 mt-2">
                      {renderUserMenu()}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* BOTTOM ROW: Navigation Links */}
          <nav className="flex gap-2 xl:gap-4">
            {navItems.map((item) => {
              const isAuthPage = item.path === '/login' || item.path === '/signup';
              const shouldDisable = isAuthenticated && isAuthPage;
              
              return (
                <Link
                  key={item.label}
                  to={shouldDisable ? '#' : item.path}
                  className={`px-3 xl:px-4 py-1 text-sm xl:text-lg font-semibold border-2 rounded-full transition ${
                    shouldDisable 
                      ? "text-gray-500 border-gray-500 cursor-not-allowed"
                      : "text-white border-transparent hover:bg-red-600 hover:border-white"
                  }`}
                  onClick={(e) => {
                    if (shouldDisable) e.preventDefault();
                  }}
                >
                  {item.label}
                </Link>
              );
            })}
          </nav>
        </div>
      </div>

      {/* --- MOBILE ONLY SECTIONS (Search bar & Menu) --- */}
      
      {/* Mobile Search Bar (Only visible when toggled on mobile) */}
      {searchOpen && (
        <div className="px-4 pb-3 lg:hidden">
          <div className="relative">
            <form onSubmit={handleSearchSubmit} className="flex items-center px-4 py-1 bg-white rounded-full focus-within:outline-none focus-within:ring-0">
              <button
                type="submit"
                className="w-4 h-4 text-black transition-colors cursor-pointer outline-none hover:text-gray-600"
              >
                <Search className="w-4 h-4" />
              </button>
              <input
                type="text"
                placeholder="Search products..."
                value={searchQuery}
                onChange={handleSearchInputChange}
                onBlur={handleSearchBlur}
                onFocus={() => searchSuggestions.length > 0 && setShowSuggestions(true)}
                className="flex-1 ml-2 text-sm text-black bg-transparent border-0 outline-none focus:outline-none focus:ring-0"
                autoFocus
              />
              {searchLoading && (
                <div className="ml-2 w-4 h-4 rounded-full border-b-2 border-gray-600 animate-spin"></div>
              )}
            </form>
            
            {/* Mobile Search Suggestions */}
            {showSuggestions && searchSuggestions.length > 0 && (
              <div className="overflow-y-auto absolute right-0 left-0 top-full z-50 mt-1 max-h-60 bg-white rounded-lg border border-gray-200 shadow-lg">
                {searchSuggestions.map((product) => (
                  <div
                    key={product.id}
                    onClick={() => handleSuggestionClick(product)}
                    className="px-4 py-3 text-black border-b border-gray-100 cursor-pointer hover:bg-gray-100 last:border-b-0"
                  >
                    <div className="text-sm font-medium text-gray-900">{product.name}</div>
                    <div className="text-xs text-gray-500">Product ID: {product.id}</div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Mobile Navigation Menu */}
      {mobileMenuOpen && (
        <div className="relative z-50 bg-gray-900 border-t border-gray-700 lg:hidden">
          <nav className="py-2">
            {navItems.map((item) => {
              const isAuthPage = item.path === '/login' || item.path === '/signup';
              const shouldDisable = isAuthenticated && isAuthPage;
              
              return (
                <Link
                  key={item.label}
                  to={shouldDisable ? '#' : item.path}
                  className={`block px-4 py-3 text-base font-medium transition ${
                    shouldDisable 
                      ? "text-gray-500 cursor-not-allowed"
                      : "text-white hover:bg-red-600 hover:text-white"
                  }`}
                  onClick={(e) => {
                    if (shouldDisable) {
                      e.preventDefault();
                    } else {
                      closeMobileMenu();
                    }
                  }}
                >
                  {item.label}
                </Link>
              );
            })}
          </nav>

          {/* Mobile Auth Section */}
          {!isAuthenticated && (
            <div className="px-4 py-3 border-t border-gray-700">
              <div className="flex flex-col gap-2">
                <button
                  onClick={() => { navigate("/login"); closeMobileMenu(); }}
                  className="px-4 py-2 w-full text-sm font-semibold text-red-600 rounded-full border-2 border-red-600 transition hover:bg-red-600 hover:text-white"
                >
                  Sign In
                </button>
                <button
                  onClick={() => { navigate("/signup"); closeMobileMenu(); }}
                  className="px-4 py-2 w-full text-sm font-semibold text-white bg-red-600 rounded-full transition hover:bg-red-700"
                >
                  Register
                </button>
              </div>
            </div>
          )}

          {/* Mobile Profile Section */}
          {isAuthenticated && (
            <div className="px-4 py-3 border-t border-gray-700">
              <div className="flex flex-col gap-2">
                {!isAdmin && (
                  <button
                    onClick={() => { handleProfileClick(); closeMobileMenu(); }}
                    className="px-4 py-2 w-full text-left text-white rounded transition hover:bg-red-600"
                  >
                    Profile
                  </button>
                )}
                <button
                  onClick={() => { handleLogout(); closeMobileMenu(); }}
                  className="px-4 py-2 w-full text-left text-white rounded transition hover:bg-red-600"
                >
                  Logout
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Click outside to close mobile menu */}
      {mobileMenuOpen && (
        <div 
          className="fixed inset-0 z-40" 
          onClick={() => setMobileMenuOpen(false)}
        />
      )}
    </header>
  );
}

// Memoize Header component to prevent unnecessary re-renders
export default React.memo(Header);
